import { useState } from 'react';
import { Link } from 'react-router-dom';
import { parseISO } from 'date-fns';
import TeamDrawer from '../../CampaignDetail/drawers/TeamDrawer';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { ITeamMembersResponse } from '~/types/campaigns';

type PhoneSet = {
    type: string;
    phone: string;
    id: number;
};
interface Props {
    patient?: any;
    metadata?: any;
    campaign?: any;
    application?: any;
    completionStatus?: any;
    teamMembers?: ITeamMembersResponse | null;
    onDataSaved?: () => void;
}

const ContactInfoTab: React.FC<Props> = ({
    patient,
    metadata,
    campaign,
    application,
    completionStatus,
    teamMembers,
    onDataSaved,
}) => {
    const person = patient?.person;
    let birthDate = person?.birth_date;
    let age: string | number = 'Unknown';
    if (birthDate) {
        birthDate = parseISO(person?.birth_date);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
        if (!hasHadBirthdayThisYear) years -= 1;
        age = years;
    }
    const [isPrimaryContactDrawerOpen, setIsPrimaryContactDrawerOpen] =
        useState(false);
    const [isEmergencyContactDrawerOpen, setIsEmergencyContactDrawerOpen] =
        useState(false);

    // Use backend completion status for contact section
    const contactSection = completionStatus?.sections?.find(
        (s: any) => s.key === 'contact'
    );

    const isContactComplete = contactSection?.is_complete ?? false;

    // Field name to human-readable label mapping based on _contact_complete method
    const fieldLabels: Record<string, string> = {
        pc_type_id: 'Primary Contact Type',
        pc_first_name: 'Primary Contact First Name',
        pc_last_name: 'Primary Contact Last Name',
        pc_contact_info: 'Primary Contact Information (Phone or Email)',
    };

    // Get missing fields from backend completion status
    const getMissingFields = (): string[] => {
        // If backend provided missing fields, use those
        if (
            contactSection?.missing_fields &&
            contactSection.missing_fields.length > 0
        ) {
            return contactSection.missing_fields;
        }

        return [];
    };

    const missingFields = getMissingFields();

    // Check if patient is their own contact
    const isSelfContact = application?.pc_type?.code === 'self';

    return (
        <>
            <div className='mb-7.5'>
                <div>
                    {isContactComplete ? (
                        <h3 className='mb-2 flex items-center text-[16px]/[24px] font-medium uppercase text-[#AFC12D]'>
                            <MdOutlineCheckCircle className='mr-2' size={24} />
                            Complete
                        </h3>
                    ) : (
                        <h3 className='mb-2 flex items-center text-[16px]/[24px] font-medium uppercase text-[#F02D3A]'>
                            <AiOutlineExclamationCircle
                                className='mr-2'
                                size={24}
                            />
                            Incomplete
                        </h3>
                    )}
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Contact Information
                    </h2>
                </div>
            </div>

            {!isContactComplete && (
                <div className='mb-7.5 rounded-lg border border-[#F02D3A] bg-[#FEF2F2] p-4'>
                    <h3 className='mb-2 flex items-center text-[14px]/[20px] font-semibold text-[#991B1B]'>
                        <AiOutlineExclamationCircle
                            className='mr-2'
                            size={20}
                        />
                        Missing Required Fields
                    </h3>
                    {missingFields.length > 0 ? (
                        <ul className='ml-6 list-disc space-y-1 text-[14px]/[20px] text-[#7F1D1D]'>
                            {missingFields.map((field: string) => (
                                <li key={field}>
                                    {fieldLabels[field] || field}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className='text-[14px]/[20px] text-[#7F1D1D]'>
                            Please complete all required fields in this section.
                        </p>
                    )}
                </div>
            )}
            <div>
                <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                    <div className='p-[16px]'>
                        <div className='mb-4 flex items-center justify-between'>
                            <h3 className='text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Primary Contact Information
                                {teamMembers?.primary_contact
                                    ?.relationship_to_patient
                                    ? ` - ${teamMembers.primary_contact.relationship_to_patient.name}`
                                    : isSelfContact
                                    ? ' - Self'
                                    : ''}
                            </h3>
                            <button
                                type='button'
                                onClick={() =>
                                    setIsPrimaryContactDrawerOpen(true)
                                }
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            >
                                {teamMembers?.primary_contact
                                    ? 'Edit'
                                    : isSelfContact
                                    ? 'Change'
                                    : 'Add'}
                            </button>
                        </div>

                        {teamMembers?.primary_contact ? (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Full Name{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div>
                                        {teamMembers.primary_contact.person
                                            ?.title &&
                                            `${teamMembers.primary_contact.person.title} `}
                                        {
                                            teamMembers.primary_contact.person
                                                ?.first_name
                                        }
                                        {teamMembers.primary_contact.person
                                            ?.middle_name &&
                                            ` ${teamMembers.primary_contact.person.middle_name}`}{' '}
                                        {
                                            teamMembers.primary_contact.person
                                                ?.last_name
                                        }
                                        {teamMembers.primary_contact.person
                                            ?.suffix &&
                                            `, ${teamMembers.primary_contact.person.suffix}`}
                                    </div>
                                </div>

                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Email Address{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    {teamMembers.primary_contact.person
                                        ?.email ? (
                                        <div className='break-all text-[#00AEB3]'>
                                            <Link
                                                to={`mailto:${teamMembers.primary_contact.person.email}`}
                                            >
                                                {
                                                    teamMembers.primary_contact
                                                        .person.email
                                                }
                                            </Link>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>

                                <div className='mb-[16px] grid grid-cols-3 gap-4'>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Phone Number{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.primary_contact.person
                                                ?.phone || ''}
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Type{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.primary_contact.person
                                                ?.phone_set?.[0]?.type || ''}
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Access{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.primary_contact
                                                .permissions?.[0] === 'full'
                                                ? 'Full Access'
                                                : teamMembers.primary_contact
                                                      .permissions?.[0] ===
                                                  'limited'
                                                ? 'Limited Access'
                                                : teamMembers.primary_contact
                                                      .permissions?.[0] ===
                                                  'no_access'
                                                ? 'No Access'
                                                : ''}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : isSelfContact && person ? (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Full Name
                                    </label>
                                    <div>
                                        {person.title && `${person.title} `}
                                        {person.first_name}
                                        {person.middle_name && ` ${person.middle_name}`} {person.last_name}
                                        {person.suffix && `, ${person.suffix}`}
                                    </div>
                                </div>

                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Email Address
                                    </label>
                                    {person.email ? (
                                        <div className='break-all text-[#00AEB3]'>
                                            <Link to={`mailto:${person.email}`}>
                                                {person.email}
                                            </Link>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>

                                <div className='mb-[16px] grid grid-cols-3 gap-4'>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Phone Number
                                        </label>
                                        <div>{person.phone || ''}</div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Type
                                        </label>
                                        <div>
                                            {(
                                                person.phone_set as PhoneSet[]
                                            )?.[0]?.type || ''}
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Access
                                        </label>
                                        <div>Full Access</div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Full Name{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                </div>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Email Address{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                </div>
                                <div className='mb-[16px] grid grid-cols-3 gap-4'>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Phone Number{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Type{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Access{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className='mb-7.5 rounded-lg border border-[#E5E5E5]'>
                    <div className='p-[16px]'>
                        <div className='mb-4 flex items-center justify-between'>
                            <h3 className='text-[16px]/[24px] font-medium uppercase text-[#131212]'>
                                Emergency Contact Information
                                {teamMembers?.emergency_contact
                                    ?.relationship_to_patient &&
                                    ` - ${teamMembers.emergency_contact.relationship_to_patient.name}`}
                            </h3>
                            <button
                                type='button'
                                onClick={() =>
                                    setIsEmergencyContactDrawerOpen(true)
                                }
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            >
                                {teamMembers?.emergency_contact
                                    ? 'Edit'
                                    : 'Add'}
                            </button>
                        </div>

                        {teamMembers?.emergency_contact ? (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Full Name{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div>
                                        {teamMembers.emergency_contact.person
                                            ?.title &&
                                            `${teamMembers.emergency_contact.person.title} `}
                                        {
                                            teamMembers.emergency_contact.person
                                                ?.first_name
                                        }
                                        {teamMembers.emergency_contact.person
                                            ?.middle_name &&
                                            ` ${teamMembers.emergency_contact.person.middle_name}`}{' '}
                                        {
                                            teamMembers.emergency_contact.person
                                                ?.last_name
                                        }
                                        {teamMembers.emergency_contact.person
                                            ?.suffix &&
                                            `, ${teamMembers.emergency_contact.person.suffix}`}
                                    </div>
                                </div>

                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Email Address{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    {teamMembers.emergency_contact.person
                                        ?.email ? (
                                        <div className='break-all text-[#00AEB3]'>
                                            <Link
                                                to={`mailto:${teamMembers.emergency_contact.person.email}`}
                                            >
                                                {
                                                    teamMembers
                                                        .emergency_contact
                                                        .person.email
                                                }
                                            </Link>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>

                                <div className='mb-[16px] grid grid-cols-3 gap-4'>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Phone Number{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.emergency_contact
                                                .person?.phone || ''}
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Type{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.emergency_contact
                                                .person?.phone_set?.[0]?.type ||
                                                ''}
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Access{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div>
                                            {teamMembers.emergency_contact
                                                .permissions?.[0] === 'full'
                                                ? 'Full Access'
                                                : teamMembers.emergency_contact
                                                      .permissions?.[0] ===
                                                  'limited'
                                                ? 'Limited Access'
                                                : teamMembers.emergency_contact
                                                      .permissions?.[0] ===
                                                  'no_access'
                                                ? 'No Access'
                                                : ''}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Full Name{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                </div>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Email Address{' '}
                                        <span className='text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div className='text-[#F02D3A]'>
                                        Required
                                    </div>
                                </div>
                                <div className='mb-[16px] grid grid-cols-3 gap-4'>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Phone Number{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Type{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-[12px]/[16px] text-[#949599]'>
                                            Access{' '}
                                            <span className='text-[#F02D3A]'>
                                                *
                                            </span>
                                        </label>
                                        <div className='text-[#F02D3A]'>
                                            Required
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <TeamDrawer
                isOpen={isPrimaryContactDrawerOpen}
                setIsOpen={setIsPrimaryContactDrawerOpen}
                isAdd={!teamMembers?.primary_contact}
                teamMember={
                    teamMembers?.primary_contact || {
                        is_primary_contact: true,
                        is_emergency_contact: false,
                        person: {},
                    }
                }
                metadata={metadata}
                campaign={campaign}
                onDataUpdate={onDataSaved}
            />

            <TeamDrawer
                isOpen={isEmergencyContactDrawerOpen}
                setIsOpen={setIsEmergencyContactDrawerOpen}
                isAdd={!teamMembers?.emergency_contact}
                teamMember={
                    teamMembers?.emergency_contact || {
                        is_primary_contact: false,
                        is_emergency_contact: true,
                        person: {},
                    }
                }
                metadata={metadata}
                campaign={campaign}
                onDataUpdate={onDataSaved}
            />
        </>
    );
};

export default ContactInfoTab;
